import { http, HttpResponse, delay } from 'msw'
import { generateFreshTransactions, getTransactions } from './data/transactions'
import { dynamicHealthMetrics } from './data'
import { transactionHandlers } from './handlers/transactions'
import { walletHandlers } from './handlers/wallets'
import { analyticsHandlers } from './handlers/analytics'
import { merchantHandlers } from './handlers/merchants'
import { settlementHandlers } from './handlers/settlements'
import { accountHandlers } from './handlers/account'
import { authHandlers } from './handlers/auth'
import { settingsHandlers } from './handlers/settings'

// Import TransactionStatus type for correct typing
import type { TransactionStatus, TypeCharge } from '../app/types/transactions'

const DEMO_DELAYS = {
  fast: 300, // Quick responses
  normal: 800, // Normal API response time
  slow: 1500, // Slow network simulation
  custom: 1000 // Configurable delay
}

// Helper function to apply demo delay
const demoDelay = async (defaultDelayType: keyof typeof DEMO_DELAYS = 'normal') => {
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('demo-mode') === 'true') {
    const configuredDelay = sessionStorage.getItem('demo-delay') as keyof typeof DEMO_DELAYS
    const delayType = configuredDelay || defaultDelayType
    await delay(DEMO_DELAYS[delayType])
  }
}

// Feed transaction type matching FreshTransaction
interface FeedTransaction {
  id: string
  amount: number
  status: TransactionStatus
  cardBrand: string
  cardMask: string
  createdAt: string
  typeCharge: TypeCharge
  customer: {
    name: string
    email: string
  }
}

// Get mock transactions for dynamic feed (using new structure)
const mockTransactions: FeedTransaction[] = getTransactions().slice(0, 50).map(t => ({
  id: t.id,
  amount: t.usd_amount,
  status: t.status,
  cardBrand: t.card_brand,
  cardMask: t.card_mask,
  createdAt: t.created_at,
  typeCharge: t.type_charge,
  customer: {
    name: t.customer.name,
    email: t.customer.email
  }
}))

// Dynamic transaction feed state for demo
class DynamicTransactionFeed {
  private transactions: FeedTransaction[]
  private lastGeneratedTime = Date.now()
  private transactionCounter = 1000 // Start with high counter to avoid ID conflicts

  constructor(initialTransactions: FeedTransaction[]) {
    this.transactions = [...initialTransactions]
  }

  getLatestTransactions(limit: number = 5): FeedTransaction[] {
    this.maybeGenerateNewTransactions()

    // Return the most recent transactions
    return this.transactions
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, limit)
  }

  private maybeGenerateNewTransactions(): void {
    const now = Date.now()
    const timeSinceLastGen = now - this.lastGeneratedTime

    // Generate new transactions based on realistic timing
    // Higher chance during demo mode, with variable intervals
    const isDemoMode = typeof sessionStorage !== 'undefined'
      && sessionStorage.getItem('demo-mode') === 'true'

    if (isDemoMode) {
      // In demo mode: more frequent transactions (every 2-5 seconds average)
      const shouldGenerate = timeSinceLastGen > 1500 && Math.random() < 0.6

      if (shouldGenerate) {
        // Generate 0-5 transactions to simulate high-volume production
        const numNewTransactions = Math.floor(Math.random() * 6) // 0, 1, 2, 3, 4, or 5
        if (numNewTransactions > 0) {
          this.generateNewTransactions(numNewTransactions)
        }
        this.lastGeneratedTime = now
      }
    } else {
      // In normal mode: less frequent but still higher volume (every 8-15 seconds average)
      const shouldGenerate = timeSinceLastGen > 6000 && Math.random() < 0.25

      if (shouldGenerate) {
        // Even in normal mode, simulate bursts of 1-3 transactions
        const numNewTransactions = Math.floor(Math.random() * 3) + 1 // 1, 2, or 3
        this.generateNewTransactions(numNewTransactions)
        this.lastGeneratedTime = now
      }
    }
  }

  private generateNewTransactions(count: number): void {
    for (let i = 0; i < count; i++) {
      const newTransactions = generateFreshTransactions(1)
      const newTransaction = newTransactions[0]

      if (newTransaction) {
        // Ensure fresh timestamp and unique ID
        newTransaction.createdAt = new Date().toISOString()
        newTransaction.id = `tx_${Date.now()}_${this.transactionCounter++}`

        // Add some randomness to make it more realistic
        this.addRealisticVariation(newTransaction)

        // Add to the beginning of our transactions list
        this.transactions.unshift(newTransaction)

        // Keep only the most recent 500 transactions to prevent memory bloat
        if (this.transactions.length > 500) {
          this.transactions = this.transactions.slice(0, 500)
        }
      }
    }
  }

  private addRealisticVariation(transaction: FeedTransaction): void {
    // Add some time variance (up to 2 minutes in the past)
    const variance = Math.random() * 2 * 60 * 1000 // 2 minutes in ms
    const adjustedTime = new Date(Date.now() - variance)
    transaction.createdAt = adjustedTime.toISOString()

    // Slightly favor confirmed transactions in real-time (people like good news!)
    if (Math.random() < 0.1) {
      transaction.status = Math.random() < 0.8 ? 'confirmed' : 'pending'
    }
  }
}

// Create the dynamic feed instance
const dynamicFeed = new DynamicTransactionFeed(mockTransactions)

// Custom handlers for dashboard and feed (not in handlers/)
const customHandlers = [
  // BFF transactions feed endpoint (real-time polling with sliding window)
  http.get('/api/bff/transactions/feed', async ({ request }) => {
    await demoDelay('fast')

    const url = new URL(request.url)
    const limit = parseInt(url.searchParams.get('limit') || '5')

    // Get latest transactions from our dynamic feed
    const latestTransactions = dynamicFeed.getLatestTransactions(limit)

    return HttpResponse.json({ transactions: latestTransactions })
  }),

  // Health metrics endpoint
  http.get('/api/bff/health/metrics', async () => {
    await demoDelay('fast') // Health metrics should be fast

    const healthMetrics = dynamicHealthMetrics.getCurrentMetrics()

    return HttpResponse.json({
      data: healthMetrics
    })
  })
]

// Export all handlers - order matters, more specific routes first
export const handlers = [
  ...customHandlers,
  ...authHandlers,
  ...settingsHandlers,
  ...analyticsHandlers,
  ...transactionHandlers,
  ...walletHandlers,
  ...merchantHandlers,
  ...settlementHandlers,
  ...accountHandlers
]
